<script lang="ts">
type Props = {
  label: string;
  value: number | string;
  formatter?: (value: number | string) => string;
  variant?: 'success' | 'danger' | 'primary' | 'warning' | 'default';
  subtitle?: string;
  icon?: string;
};

let {
  label,
  value,
  formatter,
  variant = 'default',
  subtitle,
  icon
}: Props = $props();

let formattedValue = $derived(
  formatter ? formatter(value) : value.toString()
);
</script>

<div class="stat-card {variant}">
  <div class="stat-header">
    {#if icon}
      <span class="stat-icon">{icon}</span>
    {/if}
    <span class="stat-label">{label}</span>
  </div>
  <div class="stat-value">{formattedValue}</div>
  {#if subtitle}
    <div class="stat-subtitle">{subtitle}</div>
  {/if}
</div>

<style lang="scss">
.stat-card {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  padding: var(--spacing-lg);
  background: var(--color-background);
  border: var(--border-width-thin) solid var(--color-border);
  border-radius: var(--radius-lg);
  border-left: 4px solid var(--color-border);
  transition: all var(--transition-fast);
  width: 100%;
  box-shadow: var(--shadow-md);

  &:hover {
    box-shadow: var(--shadow-lg);
    transform: translateY(-1px);
  }

  // Variant colors for left border only
  &.success {
    border-left-color: var(--color-success);
  }

  &.danger {
    border-left-color: var(--color-danger);
  }

  &.primary {
    border-left-color: var(--color-primary);
  }

  &.warning {
    border-left-color: var(--color-warning);
  }

  &.default {
    border-left-color: var(--color-text-tertiary);
  }
}

.stat-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.stat-icon {
  font-size: var(--font-size-lg);
  color: var(--color-text-secondary);
}

.stat-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-value {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  line-height: var(--line-height-tight);
}

.stat-subtitle {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

// Responsive adjustments
@media (max-width: 768px) {
  .stat-card {
    padding: var(--spacing-md);
  }

  .stat-value {
    font-size: var(--font-size-xl);
  }
}

@media (max-width: 480px) {
  .stat-value {
    font-size: var(--font-size-lg);
  }
}
</style>
